/**
 * Coupon engine — resolve and validate a typed code into a discount rule.
 *
 * Contains no pricing maths beyond delegating to `pricing-utils`, and no I/O.
 * The server passes database rows in via `fromDatabaseRow` so the `coupons`
 * table stays authoritative while the maths stays here.
 */

import {
  COUPONS,
  DEFAULT_REGION,
  FULL_DISCOUNT_ALLOWLIST,
} from "./pricing-config";
import type {
  CouponRejection,
  DiscountLayer,
  DiscountRule,
  RegionCode,
} from "./pricing-types";
import { CODE_FORMAT, normaliseCode, withinWindow } from "./pricing-utils";

/** Look a code up in the configured catalogue. */
export function findCoupon(code: string): DiscountRule | null {
  const c = normaliseCode(code);
  return COUPONS.find((x) => x.id === c) ?? null;
}

/**
 * Resolve a rule into the currency of `region`.
 *
 * A rule's `type`/`value` are denominated in the home region's currency (INR).
 * When a rule declares `regionOverrides[region]`, that override replaces the
 * type, value and label — this is how one coupon code carries a rupee amount in
 * India and a percentage off the USD MRP internationally.
 *
 * Database rows carry no overrides of their own, so the configured catalogue is
 * consulted by code. That keeps the `coupons` table authoritative for India
 * while making it impossible for a rupee value to be charged against a dollar
 * list price.
 */
export function resolveRuleForRegion(
  rule: DiscountRule,
  region: RegionCode,
): DiscountRule {
  const overrides = rule.regionOverrides ?? findCoupon(rule.id)?.regionOverrides;
  const override = overrides?.[region];
  if (!override) return rule;
  return {
    ...rule,
    type: override.type ?? rule.type,
    value: override.value,
    label: override.label ?? rule.label,
    regionOverrides: overrides,
  };
}

/**
 * True when a rule can be priced in `region` at all. A flat (money) rule is
 * only meaningful in the currency it was written in, so outside the home region
 * it needs an explicit override. Percentages are currency-neutral.
 */
export function isPriceableInRegion(
  rule: DiscountRule,
  region: RegionCode,
): boolean {
  if (region === DEFAULT_REGION) return true;
  if (String(rule.type).toLowerCase() !== "flat") return true;
  const overrides = rule.regionOverrides ?? findCoupon(rule.id)?.regionOverrides;
  return !!overrides?.[region];
}

/** Layer a database coupon belongs to, inferred from the catalogue then category. */
export function inferLayer(
  code: string,
  category?: string | null,
  partnerOrg?: string | null,
): DiscountLayer {
  const configured = findCoupon(code);
  if (configured) return configured.layer;
  const cat = String(category ?? "").toLowerCase();
  if (cat.includes("scholarship")) return "scholarship";
  if (cat.includes("campaign") || cat.includes("launch")) return "campaign";
  if (cat.includes("partner") || partnerOrg) return "partner";
  return "coupon";
}

/** Shape of a `coupons` table row, loosely typed so callers need no import. */
export interface CouponRowLike {
  id?: string;
  code: string;
  label?: string | null;
  discount_type: string;
  discount_value: number | string;
  active?: boolean | null;
  valid_from?: string | null;
  valid_until?: string | null;
  max_redemptions?: number | null;
  redemption_count?: number | null;
  partner_org?: string | null;
  category?: string | null;
}

/** Adapt a database row into an engine rule. */
export function fromDatabaseRow(row: CouponRowLike): DiscountRule {
  const code = normaliseCode(row.code);
  return {
    id: code,
    label: row.label ?? code,
    layer: inferLayer(code, row.category, row.partner_org),
    type: String(row.discount_type || "none").toLowerCase() as DiscountRule["type"],
    value: Number(row.discount_value) || 0,
    validFrom: row.valid_from ?? null,
    validUntil: row.valid_until ?? null,
    stackable: false,
    internalOnly: findCoupon(code)?.internalOnly ?? false,
    category: row.category ?? findCoupon(code)?.category ?? undefined,
    partnerOrg: row.partner_org ?? null,
    maxRedemptions: row.max_redemptions ?? null,
    active: row.active ?? true,
    // Redemption count is carried for the exhaustion check.
    ...(row.redemption_count != null
      ? { redemptionCount: Number(row.redemption_count) }
      : {}),
  } as DiscountRule & { redemptionCount?: number };
}

export interface CouponValidation {
  ok: boolean;
  rule: DiscountRule | null;
  rejection: CouponRejection | null;
}

const MESSAGES: Record<string, string> = {
  empty: "Please enter a code.",
  format:
    "Code format looks wrong. Use 3–32 letters, numbers, hyphens, or underscores.",
  not_found: "That code doesn't exist. Please double-check and try again.",
  inactive: "This code is no longer active.",
  not_yet_valid: "This code isn't active yet.",
  expired: "This code has expired.",
  exhausted: "This code has reached its redemption limit.",
  region_not_allowed: "This code isn't available for your region.",
  internal_only: "That code doesn't exist. Please double-check and try again.",
  full_discount_retired:
    "Free-access codes have been retired. Please use the personal invite link sent to your email, or contact us at hello@wekit.ai.",
};

/**
 * Validate a code. Pass `rule` when the caller already loaded it (server), or
 * omit it to resolve from configuration (client preview).
 */
export function validateCouponRule(opts: {
  code: string;
  rule?: DiscountRule | null;
  region: RegionCode;
  now: Date;
  isAdmin?: boolean;
  allowedLayers?: DiscountLayer[];
}): CouponValidation {
  const code = normaliseCode(opts.code);
  const reject = (reason: keyof typeof MESSAGES): CouponValidation => ({
    ok: false,
    rule: null,
    rejection: {
      reason: reason as CouponRejection["reason"],
      message: MESSAGES[reason],
    },
  });

  if (!code) return reject("empty");
  if (!CODE_FORMAT.test(code)) return reject("format");

  const rule = opts.rule ?? findCoupon(code);
  if (!rule) return reject("not_found");
  if (rule.active === false) return reject("inactive");

  // Internal codes must never be discoverable — same message as "not found".
  if (rule.internalOnly && !opts.isAdmin) return reject("internal_only");

  if (
    String(rule.type).toLowerCase() === "full" &&
    !FULL_DISCOUNT_ALLOWLIST.includes(code)
  ) {
    return reject("full_discount_retired");
  }

  const window = withinWindow(opts.now, rule.validFrom, rule.validUntil);
  if (!window.ok) return reject(window.reason);

  if (rule.regions && rule.regions.length > 0 && !rule.regions.includes(opts.region)) {
    return reject("region_not_allowed");
  }

  if (opts.allowedLayers && !opts.allowedLayers.includes(rule.layer)) {
    return reject("region_not_allowed");
  }

  const redemptionCount = (rule as DiscountRule & { redemptionCount?: number })
    .redemptionCount;
  if (
    rule.maxRedemptions != null &&
    redemptionCount != null &&
    redemptionCount >= rule.maxRedemptions
  ) {
    return reject("exhausted");
  }

  // A rupee amount must never be charged against a dollar list price.
  if (!isPriceableInRegion(rule, opts.region)) return reject("region_not_allowed");

  return { ok: true, rule: resolveRuleForRegion(rule, opts.region), rejection: null };
}
